import { useState } from "react";
import { Columns3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";
import { Scorecard } from "@/components/loi/Scorecard";

const usd = (n) => (n == null ? "–" : `$${Math.round(n).toLocaleString()}`);
const ROWS = [
  ["Square feet", (r) => (r.square_feet ? r.square_feet.toLocaleString() : "–")],
  ["Base rent", (r) => r.base_rent_psf || "–"],
  ["Est. yearly rent", (r) => usd(r.annual_rent), "annual_rent", "low"],
  ["TI allowance", (r) => r.ti_allowance || "–"],
  ["Free rent (months)", (r) => r.abatement_months || "–"],
  ["Contingency (days)", (r) => r.contingency_days || "–"],
  ["Build-out budget", (r) => usd(r.buildout_total), "buildout_total", "low"],
  ["Checklist score", (r) => (r.checklist_score == null ? "Not toured" : `${r.checklist_score}%`), "checklist_score", "high"],
  ["Tour photos", (r) => r.photos],
  ["Agent opens", (r) => r.agent_opens],
  ["Landlord counter", (r) => (r.counter ? `${r.counter.accept} accept · ${r.counter.counter} counter · ${r.counter.hold} hold` : "No reply yet")],
];

function best(rows, key, dir) {
  const vals = rows.map((r) => r[key]).filter((v) => v != null);
  if (vals.length < 2) return null;
  return dir === "low" ? Math.min(...vals) : Math.max(...vals);
}

export function CompareSpaces({ list }) {
  const [pick, setPick] = useState([]);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const ready = list.filter((x) => x.status === "ready");
  if (ready.length < 2) return null;
  const toggle = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length >= 3 ? (toast.error("Compare up to 3 spaces"), p) : [...p, id]));
  const go = async () => {
    setBusy(true);
    try { setRows((await api.get(`/lease-loi/compare/spaces?ids=${pick.join(",")}`)).data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="loi-compare">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Columns3 className="h-4 w-4 text-emerald-600" />Compare spaces</h3>
      <div className="mt-2 space-y-1.5">{ready.map((x) => (
        <label key={x.loi_id} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={pick.includes(x.loi_id)} onCheckedChange={() => toggle(x.loi_id)} data-testid={`loi-compare-pick-${x.loi_id}`} /><span className="truncate">{x.form.property_address}</span></label>))}</div>
      <Button size="sm" onClick={go} disabled={busy || pick.length < 2} className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="loi-compare-run">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Compare {pick.length || ""}</Button>
      {rows && (
        <div className="mt-4 overflow-x-auto -mx-4 px-4" data-testid="loi-compare-table">
          <table className="text-sm min-w-full"><thead><tr><th className="text-left p-2 w-36" />{rows.map((r) => <th key={r.loi_id} className="text-left p-2 font-semibold text-slate-900 min-w-[9rem]">{r.address}</th>)}</tr></thead>
            <tbody>{ROWS.map(([label, fmt, key, dir]) => { const b = key ? best(rows, key, dir) : null; return (
              <tr key={label} className="border-t border-slate-100"><td className="p-2 text-xs text-slate-500">{label}</td>
                {rows.map((r) => <td key={r.loi_id} className={`p-2 ${b != null && r[key] === b ? "font-bold text-emerald-700" : "text-slate-800"}`}>{fmt(r)}</td>)}</tr>); })}</tbody></table>
          <p className="text-[11px] text-slate-400 mt-2">Green marks the best value in a row. Make a build-out budget and use Tour mode on each space to fill in every row.</p>
          <Scorecard rows={rows} />
        </div>)}
    </div>
  );
}
